/**
 * Template Actions Tab
 *
 * Read-only view of sandbox actions defined for a template.
 * Shows each action's label, description, input parameters, and schema.
 */

import React from 'react';
import {
  Card,
  Text,
  Flex,
  Box,
  Heading,
  LoadingOrbit,
  TagBadge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@chatsdet/ui';
import { Lightning, CaretDown, CaretRight } from '@phosphor-icons/react';
import { useTemplateActions } from '../hooks/useTemplateActions';
import type { SandboxAction } from '../hooks/useSandboxActions';

function ActionCard({
  action,
  isOpen,
  onToggle,
}: {
  action: SandboxAction;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const properties = Object.entries(action.inputSchema.properties);
  const required = new Set(action.inputSchema.required ?? []);
  const credentialFieldMapping = action.credentialOutputConfig
    ? Object.entries(action.credentialOutputConfig.fieldMapping)
        .map(([field, value]) => `${field}=${value}`)
        .join(', ')
    : null;
  const credentialVariableMapping = action.credentialOutputConfig?.variableMapping
    ? Object.entries(action.credentialOutputConfig.variableMapping)
        .map(([name, value]) => `${name}=${value}`)
        .join(', ')
    : null;

  return (
    <Card padding="none">
      <button
        type="button"
        className="flex w-full items-center justify-between px-4 py-3 border-b border-border bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={onToggle}
      >
        <Flex align="center" gap="sm">
          {isOpen ? <CaretDown size={14} /> : <CaretRight size={14} />}
          <Lightning size={16} className="text-muted-foreground" />
          <Text size="sm" weight="medium">{action.label}</Text>
          <TagBadge name={action.slug} color="gray" hideDot />
          {!action.enabled && <TagBadge name="disabled" color="yellow" hideDot />}
          {action.credentialOutputConfig && (
            <TagBadge name="produces credential" color="green" hideDot />
          )}
        </Flex>
        {properties.length > 0 && (
          <Text size="xs" className="text-muted-foreground">
            {properties.length} param{properties.length !== 1 && 's'}
          </Text>
        )}
      </button>
      {isOpen && (
        <Box className="px-4 py-3 space-y-3">
          {action.description && (
            <Text size="sm" className="text-muted-foreground">
              {action.description}
            </Text>
          )}

          {action.credentialOutputConfig && (
            <Box className="space-y-2">
              <Text size="xs" weight="medium" className="text-muted-foreground uppercase tracking-wider">
                Ad Hoc Credential Output
              </Text>
              <div className="rounded-lg border border-border bg-muted/20 px-3 py-2">
                <Text size="xs" className="text-muted-foreground">
                  This action can seed login credentials for sandbox-backed ad hoc tests.
                </Text>
                <Text size="xs" className="mt-1 font-mono break-all">{credentialFieldMapping}</Text>
                {credentialVariableMapping && (
                  <>
                    <Text size="xs" className="mt-2 text-muted-foreground">
                      Persona variables filled from action output (injected as $var.USER.&lt;NAME&gt;):
                    </Text>
                    <Text size="xs" className="mt-1 font-mono break-all">
                      {credentialVariableMapping}
                    </Text>
                  </>
                )}
              </div>
            </Box>
          )}

          {properties.length > 0 && (
            <Box className="space-y-2">
              <Text size="xs" weight="medium" className="text-muted-foreground uppercase tracking-wider">
                Parameters
              </Text>
              <div className="rounded-lg border border-border overflow-hidden">
                <Table aria-label="Action parameters">
                  <TableHeader>
                    <TableHead isRowHeader className="h-9 text-xs">Name</TableHead>
                    <TableHead className="h-9 text-xs">Type</TableHead>
                    <TableHead className="h-9 text-xs">Description</TableHead>
                  </TableHeader>
                  <TableBody>
                    {properties.map(([key, prop]) => (
                      <TableRow key={key}>
                        <TableCell className="py-2 font-mono text-xs">
                          {key}
                          {required.has(key) && <span className="text-destructive ml-1">*</span>}
                        </TableCell>
                        <TableCell className="py-2 text-xs text-muted-foreground">
                          {prop.type}
                          {prop.enum && ` (${prop.enum.join(' | ')})`}
                          {prop.sensitive && (
                            <TagBadge name="sensitive" color="yellow" hideDot className="ml-1" />
                          )}
                        </TableCell>
                        <TableCell className="py-2 text-xs text-muted-foreground">
                          {prop.description ?? '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Box>
          )}

          <Flex align="center" gap="md" className="text-xs text-muted-foreground pt-1">
            <Text size="xs">Timeout: {(action.timeoutMs / 1000).toFixed(0)}s</Text>
          </Flex>
        </Box>
      )}
    </Card>
  );
}

export function TemplateActionsTab({ templateId }: { templateId: string }) {
  const actionsQuery = useTemplateActions(templateId);
  const actions = actionsQuery.data ?? [];
  const [openSlugs, setOpenSlugs] = React.useState<Set<string>>(new Set());

  const toggleAction = (slug: string) => {
    setOpenSlugs((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  if (actionsQuery.isLoading) {
    return (
      <Flex justify="center" className="py-12">
        <LoadingOrbit />
      </Flex>
    );
  }

  if (actions.length === 0) {
    return (
      <Box className="rounded-lg border border-dashed border-border px-4 py-6">
        <Flex direction="column" gap="sm" align="center">
          <Text className="text-center text-muted-foreground">
            No actions are defined for this template yet.
          </Text>
          <Text size="sm" className="max-w-xl text-center text-muted-foreground">
            Sandbox-backed ad hoc tests require an enabled action with credential output so the
            pool can seed a login. Use the checklist&apos;s Ad Hoc Test Ready step or the
            `canary sandbox action create --credential-output-config ...` flow to add one.
          </Text>
        </Flex>
      </Box>
    );
  }

  return (
    <div className="space-y-3">
      <Flex align="center" gap="sm">
        <Heading level={4}>Actions</Heading>
        <TagBadge name={actions.length} color="gray" hideDot />
      </Flex>
      {actions.map((action) => (
        <ActionCard
          key={action.slug}
          action={action}
          isOpen={openSlugs.has(action.slug)}
          onToggle={() => toggleAction(action.slug)}
        />
      ))}
    </div>
  );
}
